import React, { useState, useEffect } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Send, Trash2, Truck, FileText, Mail, CreditCard, Banknote, Zap } from "lucide-react";
import { toast } from "sonner";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";
import { logError } from "@/lib/log";

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Drawer for a single order. Surfaces every legal status transition
 * based on the order's current state. Reuses the same dialog whether
 * the order is a quote, pending portal order, placed, paid, etc.
 */
export default function OrderActionsDialog({ order, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  // Hydrate from the order's pre-selected method (set at creation/edit) so admins
  // see exactly what the customer is expecting; editable before payment is recorded.
  const [fulfillAs, setFulfillAs] = useState(order.fulfillment_method || "invoice");
  // Pre-populate from the order's saved delivery_date (set at creation). Falls
  // back to today if the order was created before this field existed.
  const [deliveryDate, setDeliveryDate] = useState(order.delivery_date || todayISO());
  // Recovery: convert an already-paid pickup order into a delivery ticket.
  const [convertDate, setConvertDate] = useState(order.delivery_date || todayISO());
  const [converting, setConverting] = useState(false);
  // Card-on-file state for the customer that owns this order. Loaded on mount
  // when the order is in "placed" status so admin can offer a one-click
  // saved-card charge or a fresh card entry alongside the manual mark-paid.
  const [customerCof, setCustomerCof] = useState(null);
  const [chargeCardOpen, setChargeCardOpen] = useState(false);

  useEffect(() => {
    if (order.status !== "placed" || !order.customer_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${order.customer_id}`);
        if (!cancelled) setCustomerCof((data || {}).card_on_file || null);
      } catch (e) {
        logError("OrderActionsDialog: fetch customer card-on-file failed (non-fatal):", e);
      }
    })();
    return () => { cancelled = true; };
  }, [order.status, order.customer_id]);

  const status = order.status;
  const isQuote = order.type === "quote";

  const convertToTicket = async () => {
    if (!window.confirm(`Open a delivery ticket for ${convertDate} so the driver sees this order?`)) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/convert-to-ticket`, { delivery_date: convertDate });
      toast.success(`Delivery ticket created for ${data.delivery_date}.`);
      onChanged?.({ ...order, fulfillment_method: "ticket", fulfillment: { method: "ticket", ticket_id: data.ticket_id }, delivery_date: data.delivery_date });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setConverting(false); }
  };

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const updated = await fn();
      successMsg && toast.success(successMsg);
      onChanged && onChanged(updated);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  // Persist the fulfillment choice + scheduled date to the order so they survive
  // reload + match the customer-facing portal status. Only meaningful for
  // non-final statuses.
  const persistFulfillment = async (fmValue, dateValue) => {
    const sameMethod = fmValue === order.fulfillment_method;
    const sameDate = dateValue === (order.delivery_date || "");
    if (sameMethod && sameDate) return;
    try {
      const r = await api.put(`/orders/${order.id}`, {
        items: (order.items || []).map((it) => ({
          product_id: it.product_id || null,
          description: it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          taxable: !!it.taxable,
        })),
        notes: order.notes || "",
        tax_rate: order.tax_rate,
        fulfillment_method: fmValue,
        delivery_date: fmValue === "ticket" ? (dateValue || null) : null,
      });
      onChanged && onChanged(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const onFulfillChange = (value) => {
    setFulfillAs(value);
    persistFulfillment(value, deliveryDate);
  };

  const onDeliveryDateChange = (value) => {
    setDeliveryDate(value);
    persistFulfillment(fulfillAs, value);
  };

  const sendQuote = () => run(async () => (await api.post(`/orders/${order.id}/send-quote`)).data, "Quote emailed to customer");
  const acceptQuote = () => run(async () => (await api.post(`/orders/${order.id}/accept-quote`)).data, "Quote accepted → placed");
  const decline = () => {
    if (!window.confirm("Decline this order? The customer will see it as declined.")) return;
    run(async () => (await api.post(`/orders/${order.id}/decline`)).data, "Order declined");
  };
  const remove = () => {
    if (!window.confirm("Delete this order? This cannot be undone.")) return;
    run(async () => { await api.delete(`/orders/${order.id}`); onClose(); return null; }, "Order deleted");
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display mono-num">{order.number}</DialogTitle>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-secondary text-foreground rounded-sm" data-testid="actions-status">
              {status.replace("_", " ")}
            </span>
          </div>
          <DialogDescription>
            {order.business_name || order.customer_name} · {fmtDate(order.created_at?.slice(0, 10))} · {fmtMoney(order.total)}
          </DialogDescription>
        </DialogHeader>

        {/* Line items */}
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2 overline bg-secondary/40">
            <div className="col-span-6">Item</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-2 text-right">Total</div>
          </div>
          {(order.items || []).map((it, i) => (
            <div key={`it-${i}-${it.description}`} className="grid grid-cols-12 px-4 py-2 border-b border-border last:border-b-0 text-sm">
              <div className="col-span-6">
                {it.description}
                {!it.taxable && <span className="text-xs text-muted-foreground ml-2">(non-tax)</span>}
              </div>
              <div className="col-span-2 text-right mono-num">{it.quantity}</div>
              <div className="col-span-2 text-right mono-num">{fmtMoney(it.unit_price)}</div>
              <div className="col-span-2 text-right mono-num font-semibold">{fmtMoney(it.amount)}</div>
            </div>
          ))}
          <div className="px-4 py-3 bg-secondary/30 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="mono-num">{fmtMoney(order.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({order.tax_rate}%)</span><span className="mono-num">{fmtMoney(order.tax)}</span></div>
            <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total</span><span className="mono-num">{fmtMoney(order.total)}</span></div>
          </div>
        </div>

        {order.notes && (
          <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 text-sm" data-testid="order-notes-display">
            <p className="font-semibold text-xs uppercase tracking-wider text-amber-800 mb-1">Notes</p>
            {order.notes}
          </div>
        )}

        {order.fulfillment && (
          <div className="text-xs text-muted-foreground" data-testid="order-fulfillment-info">
            Fulfilled as {order.fulfillment.method}
            {order.fulfillment.ticket_id && ` — ticket ${order.fulfillment.ticket_id}`}
            {order.fulfillment.invoice_id && ` — invoice ${order.fulfillment.invoice_id}`}
          </div>
        )}

        {/* Recovery: order was paid as "pickup" but needs a driver delivery. */}
        {["paid", "fulfilled"].includes(status) && !order.fulfillment?.ticket_id && (
          <div className="border border-primary/40 bg-primary/5 rounded-sm p-4 space-y-2" data-testid="convert-to-ticket-section">
            <p className="text-sm font-semibold">Need a driver to deliver this?</p>
            <p className="text-xs text-muted-foreground">
              This order was paid as Pickup. Open a delivery ticket so the driver sees it in their day.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={convertDate}
                onChange={(e) => setConvertDate(e.target.value)}
                className="rounded-sm border border-input px-3 h-9 text-sm bg-background mono-num"
                data-testid="convert-to-ticket-date"
              />
              <Button
                onClick={convertToTicket}
                disabled={converting}
                size="sm"
                className="rounded-sm"
                data-testid="convert-to-ticket-button"
              >
                {converting ? "Creating…" : "Create delivery ticket"}
              </Button>
            </div>
          </div>
        )}

        {/* Fulfillment route — visible for any non-finalised order so the admin
            (and any reviewer) knows where this order will land once paid. */}
        {!["fulfilled", "declined", "cancelled"].includes(status) && (
          <div className="border border-border rounded-sm p-4 space-y-3" data-testid="fulfillment-section">
            <p className="overline">When paid, this order will</p>
            <RadioGroup value={fulfillAs} onValueChange={onFulfillChange} className="grid sm:grid-cols-2 gap-2">
              <label className="flex items-start gap-3 cursor-pointer border border-border bg-background rounded-sm p-3 hover:border-primary/50">
                <RadioGroupItem value="invoice" id="r-invoice" data-testid="fulfill-as-invoice" />
                <div className="flex-1">
                  <div className="font-medium text-sm flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Become a paid invoice</div>
                  <div className="text-xs text-muted-foreground">Pickup / no driver visit.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer border border-border bg-background rounded-sm p-3 hover:border-primary/50">
                <RadioGroupItem value="ticket" id="r-ticket" data-testid="fulfill-as-ticket" />
                <div className="flex-1">
                  <div className="font-medium text-sm flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Open a delivery ticket</div>
                  <div className="text-xs text-muted-foreground">Driver Day picks it up.</div>
                </div>
              </label>
            </RadioGroup>
            {fulfillAs === "ticket" && (
              <div className="flex items-center gap-3 border-t border-border pt-3">
                <Label className="text-xs font-semibold whitespace-nowrap">Delivery date</Label>
                <Input type="date" className="rounded-sm h-9 max-w-[200px]"
                       value={deliveryDate}
                       onChange={(e) => onDeliveryDateChange(e.target.value)}
                       data-testid="fulfill-delivery-date" />
                <span className="text-xs text-muted-foreground hidden sm:inline">— driver sees this on Driver Day</span>
              </div>
            )}
          </div>
        )}

        {/* Payment + fulfillment actions: admin picks how the customer is paying. */}
        {status === "placed" && (
          <div className="border border-primary/30 bg-primary/5 rounded-sm p-4 space-y-2" data-testid="mark-paid-section">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Collect payment {fulfillAs === "ticket" ? "& open delivery ticket" : "& create invoice"}
            </p>
            {customerCof?.token && (
              <Button
                onClick={() => run(
                  async () => (await api.post(`/payments/cardknox/charge-order-saved`, { order_id: order.id })).data,
                  `Charged ${(customerCof.brand || "card").toUpperCase()} •••• ${customerCof.last4 || "????"} — ${fulfillAs === "ticket" ? "ticket created" : "invoice created"}`,
                )}
                disabled={busy}
                className="rounded-sm w-full"
                data-testid="charge-card-on-file"
              >
                <Zap className="w-4 h-4 mr-2" />
                Charge card on file ({(customerCof.brand || "CARD").toUpperCase()} •••• {customerCof.last4 || "????"})
              </Button>
            )}
            <Button
              variant={customerCof?.token ? "outline" : "default"}
              onClick={() => setChargeCardOpen(true)}
              disabled={busy}
              className="rounded-sm w-full"
              data-testid="charge-new-card"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Charge a new credit card
            </Button>
            <Button
              variant="outline"
              onClick={() => run(async () => (await api.post(`/orders/${order.id}/mark-paid`, {
                method: "manual",
                // No fulfill_as — server reads order.fulfillment_method (set when the
                // admin picked the radio above). Single source of truth.
                delivery_date: fulfillAs === "ticket" ? deliveryDate : null,
              })).data, fulfillAs === "ticket" ? "Paid — open ticket created for the driver" : "Paid — invoice generated")}
              disabled={busy}
              className="rounded-sm w-full"
              data-testid="mark-paid-confirm"
            >
              <Banknote className="w-4 h-4 mr-2" />
              Mark paid manually (cash / check / already paid)
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {isQuote && (status === "draft_quote" || status === "quote_sent") && (
            <>
              <Button variant="outline" onClick={sendQuote} disabled={busy} className="rounded-sm" data-testid="send-quote-button">
                <Send className="w-4 h-4 mr-2" /> {status === "draft_quote" ? "Send to customer" : "Re-send"}
              </Button>
              <Button variant="outline" onClick={acceptQuote} disabled={busy} className="rounded-sm" data-testid="accept-quote-button">
                <Mail className="w-4 h-4 mr-2" /> Accept on customer&apos;s behalf
              </Button>
            </>
          )}
          {!["paid", "fulfilled", "declined", "cancelled"].includes(status) && (
            <Button variant="ghost" onClick={decline} disabled={busy} className="rounded-sm text-destructive" data-testid="decline-order-button">
              <XCircle className="w-4 h-4 mr-2" /> Decline
            </Button>
          )}
          {!["paid", "fulfilled"].includes(status) && (
            <Button variant="ghost" onClick={remove} disabled={busy} className="rounded-sm text-destructive" data-testid="delete-order-button">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
          <Button onClick={onClose} className="rounded-sm">Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Cardknox iFields dialog — opened from "Charge a new credit card". Pays
          the order directly; the backend then auto-marks it paid + fulfilled. */}
      <CardknoxPayDialog
        open={chargeCardOpen}
        onClose={() => setChargeCardOpen(false)}
        invoice={{ id: order.id, number: order.number, total: order.total }}
        mode="pay-order"
        onPaid={() => {
          setChargeCardOpen(false);
          toast.success(fulfillAs === "ticket"
            ? "Card charged — delivery ticket created."
            : "Card charged — invoice generated.");
          onChanged?.({ ...order, status: "paid" });
        }}
      />
    </Dialog>
  );
}
